import { Link, Typography, useMediaQuery, Grid } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useUser } from 'hooks';

import { RootState } from 'storage/store';
import ValidationSchema from 'validations/LoginForm';
import appTheme from 'App.theme';
import {
  Button,
  QuestionAndAnswer,
  TextField as TextFieldComponent
} from 'components';
import { SimplePasswordField } from 'components/SimplePasswordField';
import { MessageTypes, UserCredentials } from 'storage/user/user.model';
import { Url } from 'utils/urls';

import FormWrapper from './styled/FormWrapper';
import ErrorMessage from './ErrorMessage';
import { onLoginFormSubmitType } from './Login';
import InfoAboutAlternativeLogin from './components/InfoAboutAlternativeLogin';
import AcceptRegulationsWithInfo from '../../components/AcceptRegulationsWithInfo/AcceptRegulationsWithInfo';

interface ILoginForm {
  onLoginFormSubmit: onLoginFormSubmitType<UserCredentials>;
}

export const LoginForm = ({ onLoginFormSubmit }: ILoginForm) => {
  const { t } = useTranslation();
  const desktopBreakpoint = useMediaQuery(appTheme.breakpoints.up('lg'));
  const user = useUser();

  const schema = ValidationSchema(t, user?.regulationsAcceptanceRequired);
  const { kisaLoginPageVisibility } = useSelector(
    (state: RootState) => state.application
  );

  return (
    <Grid
      size={{ xs: 12, lg: 7 }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        paddingLeft: '23px',
        [appTheme.breakpoints.up('lg')]: {
          paddingTop: '80px'
        },
        [appTheme.breakpoints.down('lg')]: {
          marginTop: '86px',
          paddingLeft: '9px'
        },
        [appTheme.breakpoints.down('md')]: {
          alignItems: 'flex-start',
          marginTop: '86px',
          paddingLeft: '0'
        }
      }}
    >
      <FormWrapper disableGutters>
        <Formik
          onSubmit={onLoginFormSubmit}
          validationSchema={schema}
          initialValues={{ login: '', password: '' }}
          validateOnChange={false}
        >
          <Form noValidate>
            {kisaLoginPageVisibility && <InfoAboutAlternativeLogin />}
            <Typography
              variant={desktopBreakpoint ? 'h3' : 'h4'}
              sx={{ pt: 2 }}
            >
              {t('PAGE_LOGIN_TITLE')}
            </Typography>
            <ErrorMessage messageType={user?.userStatus} />
            <Grid container>
              <Grid size={{ xs: 12 }}>
                <Field
                  type="email"
                  name="login"
                  labelText={t('FORM_FIELD_EMAIL')}
                  label={t('FORM_FIELD_EMAIL')}
                  component={TextFieldComponent}
                  showIcon
                  fullWidth
                  slotProps={{
                    htmlInput: {
                      inputMode: 'email'
                    }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Field
                  type="password"
                  name="password"
                  label={t('FORM_FIELD_PASSWORD')}
                  labelText={t('FORM_FIELD_PASSWORD')}
                  component={SimplePasswordField}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                {user?.userStatus === MessageTypes.blocked ? (
                  <Link href={Url.unblockAccount} variant="body1">
                    {t('PAGE_LOGIN_ACCOUNT_UNLOCK')}
                  </Link>
                ) : (
                  <Link href={Url.resetPassword} variant="body1">
                    {t('PAGE_LOGIN_FORGOTTEN_PASSWORD')}
                  </Link>
                )}
              </Grid>
              <Grid>
                <AcceptRegulationsWithInfo />
              </Grid>
              <Grid size={{ xs: 12 }} style={{ margin: '30px 0 25px' }}>
                <Button
                  size="large"
                  color="primary"
                  type="submit"
                  variant="contained"
                >
                  {t('BUTTON_LOGIN')}
                </Button>
              </Grid>
              <Grid size={{ xs: 12 }} style={{ margin: '10px 0 20px 0' }}>
                <QuestionAndAnswer
                  question={t('PAGE_LOGIN_CREATE_ACCOUNT_MESSAGE')}
                  answer={{
                    href: Url.register,
                    text: t('PAGE_LOGIN_CREATE_ACCOUNT_LINK')
                  }}
                />
              </Grid>
            </Grid>
          </Form>
        </Formik>
      </FormWrapper>
    </Grid>
  );
};

export default LoginForm;
